import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Badge, Rating, StatusBadge } from "@/components/ui/Chips";
import { SafeImage } from "@/components/ui/Blocks";
import { Button, ButtonLink } from "@/components/ui/Button";
import { FloorPlanDrawing } from "@/components/floorplan/FloorPlan";
import { LockBadge, Watermark } from "@/components/floorplan/Watermark";
import { CTA_BY_STATUS } from "@/lib/constants";
import { amenityIcon } from "./amenities";
import { formatArea, formatDate, formatINR, relativeDate } from "@/lib/format";
import { useLicenceStore } from "@/store/licences";
import { staggerChild } from "@/motion/variants";
import type { BlogPost, CompletedProject, FloorPlan, Job, Service, TurnkeyProject } from "@/types";

const CARD_MOTION = {
  variants: staggerChild,
  whileHover: { y: -5 },
  transition: { duration: 0.26 },
} as const;

/* -------------------------------------------------------------------------- */
/* Completed project                                                          */
/* -------------------------------------------------------------------------- */

export function ProjectCard({ project }: { project: CompletedProject }) {
  const to = `/projects/${project.id}`;

  return (
    <motion.article className="project-card" {...CARD_MOTION}>
      <div className="project-card__media">
        <SafeImage src={project.cover} alt={`${project.name} — ${project.type} project in ${project.city}`} />
        <span className="project-card__cat">
          <Badge tone="glass">{project.type}</Badge>
        </span>
        <div className="project-card__overlay">
          <span className="project-card__view" aria-hidden="true">
            View Project
            <Icon name="arrow-right" />
          </span>
        </div>
      </div>

      <div className="project-card__body">
        <div className="project-card__meta">
          <span>{project.city}</span>
          <span>{project.year}</span>
        </div>
        <h3 className="project-card__title">
          <Link to={to}>{project.name}</Link>
        </h3>
        <p className="small" style={{ margin: 0 }}>
          {project.excerpt}
        </p>
        <div className="project-card__foot">
          <span>{project.area}</span>
          <span className="link-arrow">
            View Project
            <Icon name="arrow-right" />
          </span>
        </div>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------------------------------- */
/* Turn-key property                                                          */
/* -------------------------------------------------------------------------- */

export function PropertyCard({
  project,
  onEnquire,
}: {
  project: TurnkeyProject;
  onEnquire: (project: TurnkeyProject) => void;
}) {
  const to = `/turnkey/${project.id}`;
  const cta = CTA_BY_STATUS[project.status];
  const soldOut = project.status === "sold-out";

  return (
    <motion.article className="property-card" {...CARD_MOTION}>
      <div className="property-card__media">
        <SafeImage src={project.cover} alt={`${project.name} — ${project.propertyType} in ${project.location}`} />
        <div className="property-card__badges">
          <StatusBadge status={project.status} />
          <span className="property-card__stage">{project.possession}</span>
        </div>
      </div>

      <div className="property-card__body">
        <div className="property-card__head">
          <h3 className="property-card__title">
            <Link to={to}>{project.name}</Link>
          </h3>
          <span className="property-card__loc">
            <Icon name="map-pin" />
            {project.location}
          </span>
        </div>

        <dl className="property-specs">
          <div>
            <dt>Type</dt>
            <dd>{project.propertyType}</dd>
          </div>
          <div>
            <dt>Configuration</dt>
            <dd>{project.configurations.join(" · ")}</dd>
          </div>
          <div>
            <dt>Area</dt>
            <dd>{project.areaRange}</dd>
          </div>
          <div>
            <dt>Possession</dt>
            <dd>{project.possession}</dd>
          </div>
        </dl>

        <div className="property-card__amenities">
          {project.amenities.slice(0, 3).map((amenity) => (
            <span key={amenity} className="amenity-pill">
              <Icon name={amenityIcon(amenity)} />
              {amenity}
            </span>
          ))}
          {project.amenities.length > 3 ? (
            <span className="amenity-pill">+{project.amenities.length - 3} more</span>
          ) : null}
        </div>

        <div className="property-card__price">
          {soldOut ? (
            <span className="amount" style={{ color: "var(--text-muted)" }}>
              Sold out
            </span>
          ) : (
            <>
              <span className="from">Starting</span>
              <span className="amount">{project.priceLabel}</span>
            </>
          )}
        </div>

        <div className="property-card__actions">
          <ButtonLink to={to} variant="primary" size="sm">
            View Project
          </ButtonLink>
          <Button variant="ghost" size="sm" onClick={() => onEnquire(project)}>
            {cta.label}
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------------------------------- */
/* Floor plan                                                                 */
/* -------------------------------------------------------------------------- */

export function PlanCard({ plan }: { plan: FloorPlan }) {
  const owned = useLicenceStore((state) => Boolean(state.licences[plan.id]));
  const to = `/floor-plans/${plan.id}`;

  return (
    <motion.article className="plan-card" {...CARD_MOTION}>
      <div className={`plan-card__canvas fp-sheet${owned ? "" : " fp-protected"}`}>
        <div className={owned ? undefined : "fp-blur--soft"}>
          <FloorPlanDrawing plan={plan} locked={!owned} thumb />
        </div>
        {owned ? null : <Watermark mark={`${plan.code} PREVIEW `} />}
        <LockBadge owned={owned} />
        <span className="plan-card__code">{plan.code}</span>
      </div>

      <div className="plan-card__body">
        <div className="flex-between" style={{ alignItems: "flex-start" }}>
          <h3 className="plan-card__title">
            <Link to={to}>{plan.name}</Link>
          </h3>
          <Rating value={plan.rating} />
        </div>

        <span className="plan-card__type">
          <Icon name="building" />
          {plan.infrastructure} · {plan.style}
        </span>

        <dl className="plan-specs">
          <div>
            <dt>Surface area</dt>
            <dd>{formatArea(plan.builtUpArea)}</dd>
          </div>
          <div>
            <dt>Dimensions</dt>
            <dd>
              {plan.dim.w} × {plan.dim.d} ft
            </dd>
          </div>
          <div>
            <dt>Flooring</dt>
            <dd>
              {plan.flooring[0]}
              {plan.flooring.length > 1 ? ` +${plan.flooring.length - 1}` : ""}
            </dd>
          </div>
          <div>
            <dt>Configuration</dt>
            <dd>
              {plan.bedrooms ? `${plan.bedrooms} BHK · ` : ""}
              {plan.floors} {plan.floors > 1 ? "levels" : "level"}
            </dd>
          </div>
        </dl>

        <div className="plan-card__formats">
          {plan.formats.map((format) => (
            <span key={format} className="format-chip">
              {format}
            </span>
          ))}
        </div>

        <div className="plan-card__foot">
          <span className="plan-price">
            {owned ? (
              <>
                <span className="amount">Licensed</span>
                <span className="note">Full set available</span>
              </>
            ) : (
              <>
                <span className="amount">{formatINR(plan.price)}</span>
                <span className="note">
                  <span className="mrp">{formatINR(plan.mrp)}</span> · {plan.sales} sold
                </span>
              </>
            )}
          </span>
          <ButtonLink to={to} variant="primary" size="sm">
            {owned ? "Open plan" : "View & unlock"}
          </ButtonLink>
        </div>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------------------------------- */
/* Editorial                                                                  */
/* -------------------------------------------------------------------------- */

export function BlogCard({ post }: { post: BlogPost }) {
  const to = `/journal/${post.id}`;

  return (
    <motion.article className="blog-card" variants={staggerChild}>
      <Link to={to} className="blog-card__media" tabIndex={-1} aria-hidden="true">
        <SafeImage src={post.cover} alt={post.title} />
        <span className="blog-card__cat">
          <Badge tone="glass">{post.category}</Badge>
        </span>
      </Link>
      <h3 className="blog-card__title">
        <Link to={to}>{post.title}</Link>
      </h3>
      <p className="blog-card__excerpt">{post.excerpt}</p>
      <div className="blog-card__meta">
        <span>{formatDate(post.date)}</span>
        <span className="dot" />
        <span>{post.readTime} min read</span>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------------------------------- */
/* Service                                                                    */
/* -------------------------------------------------------------------------- */

export function ServiceCard({ service }: { service: Service }) {
  const to = `/services/${service.id}`;

  return (
    <motion.article className="service-card" {...CARD_MOTION}>
      <span className="service-card__icon">
        <Icon name={service.icon as IconName} />
      </span>
      <h3>
        <Link to={to}>{service.name}</Link>
      </h3>
      <p>{service.summary}</p>
      <ul className="service-card__list">
        {service.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
      <div className="service-card__foot">
        <Link className="link-arrow" to={to}>
          Explore service
          <Icon name="arrow-right" />
        </Link>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------------------------------- */
/* Job                                                                        */
/* -------------------------------------------------------------------------- */

export function JobCard({ job }: { job: Job }) {
  const to = `/careers/${job.id}`;

  return (
    <motion.article className="job-card" variants={staggerChild} whileHover={{ y: -3 }}>
      <div className="job-card__main">
        <h3 className="job-card__title">
          <Link to={to}>{job.title}</Link>
        </h3>
        <div className="job-card__meta">
          <span>
            <Icon name="briefcase" />
            {job.department}
          </span>
          <span>
            <Icon name="map-pin" />
            {job.location}
          </span>
          <span>
            <Icon name="clock" />
            {job.type}
          </span>
          <span>
            <Icon name="award" />
            {job.experience}
          </span>
        </div>
      </div>
      <div className="job-card__side">
        <span className="job-card__posted">{relativeDate(job.posted)}</span>
        <ButtonLink to={to} variant="ghost" size="sm" icon="arrow-right">
          View Role
        </ButtonLink>
      </div>
    </motion.article>
  );
}

/* -------------------------------------------------------------------------- */
/* Search result                                                              */
/* -------------------------------------------------------------------------- */

const KIND_LABEL: Record<string, string> = {
  project: "Completed Project",
  turnkey: "Turn-Key Project",
  plan: "Floor Plan",
  blog: "Journal",
  service: "Service",
};

export function ResultCard({
  result,
  term,
}: {
  result: { kind: string; title: string; excerpt: string; image?: string; href: string };
  term: string;
}) {
  return (
    <motion.article className="result-item" variants={staggerChild} whileHover={{ y: -3 }}>
      <div className="result-item__media">
        {result.image ? (
          <SafeImage src={result.image} alt={result.title} />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              background: "var(--green-soft)",
              color: "var(--green)",
            }}
          >
            <Icon name="drafting" size={22} />
          </div>
        )}
      </div>
      <div className="result-item__body">
        <span className="mono-label">{KIND_LABEL[result.kind] ?? result.kind}</span>
        <h3>
          <Link to={result.href}>
            <Highlight text={result.title} term={term} />
          </Link>
        </h3>
        <p>
          <Highlight text={result.excerpt} term={term} />
        </p>
      </div>
      <div className="result-item__type">
        <ButtonLink to={result.href} variant="ghost" size="sm" icon="arrow-right">
          Open
        </ButtonLink>
      </div>
    </motion.article>
  );
}

/** Wraps matches in <mark> without dangerouslySetInnerHTML. */
export function Highlight({ text, term }: { text: string; term: string }) {
  if (!term.trim()) return <>{text}</>;

  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase() ? <mark key={i}>{part}</mark> : part,
      )}
    </>
  );
}

